import { useState, useEffect } from 'react';
import { useBranch } from '../contexts/BranchContext';
import Toast from '../components/Toast';

const DRIVERS = [
  { value: 'epson', label: 'EPSON' },
  { value: 'xprinter', label: 'XPRINTER' }
];

function blankPrinter() {
  return {
    id: undefined,
    name: '',
    role: '',
    driver: 'epson',
    type: 'network',
    ip: '',
    port: 9100,
    usbPort: ''
  };
}

export default function PrinterSettings({ apiUrl }) {
  const { currentBranch, effectiveBranchId, isAllBranches, loading: branchLoading } = useBranch();
  const [printers, setPrinters] = useState([]);
  const [defaultCheckoutPrinterId, setDefaultCheckoutPrinterId] = useState('');
  const [defaultKitchenPrinterId, setDefaultKitchenPrinterId] = useState('');
  const [status, setStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [message, setMessage] = useState(null);

  // ต้องเลือกสาขาที่ชัดเจนก่อนถึงจะโหลด/แก้ค่าได้ — ตั้งค่านี้เขียนทับ config จริง
  // ของสาขาที่ระบุทันที เคยเกิดเหตุจริงที่ตั้งใจจะแก้สาขาหนึ่งแต่ดันไปทับอีกสาขา
  // เพราะหน้านี้ไม่เคยโชว์ให้เห็นเลยว่ากำลังแก้ของสาขาไหนอยู่
  const blocked = isAllBranches || !effectiveBranchId;

  useEffect(() => {
    if (blocked) {
      setLoading(false);
      return;
    }
    loadConfig();
  }, [effectiveBranchId, blocked]);

  useEffect(() => {
    if (blocked || branchLoading) return;
    checkStatus();
  }, [effectiveBranchId, blocked, branchLoading]);

  const token = () => localStorage.getItem('adminToken');
  const branchHeader = () => ({ 'X-Branch-Id': effectiveBranchId });

  const loadConfig = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/printer/config`, {
        headers: { Authorization: `Bearer ${token()}`, ...branchHeader() }
      });
      const data = await res.json();
      if (data.data) {
        setPrinters(data.data.printers || []);
        setDefaultCheckoutPrinterId(data.data.defaultCheckoutPrinterId || '');
        setDefaultKitchenPrinterId(data.data.defaultKitchenPrinterId || '');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async () => {
    try {
      // ยิงผ่าน apiUrl (cloud) เสมอ — cloud proxy ต่อไปเครื่อง daemon จริงของสาขาเอง
      // ถ้าจำเป็น (ดู printer.route.js) ไม่ต้องรู้/ยิง tunnel ตรงจากเบราว์เซอร์เลย
      const res = await fetch(`${apiUrl}/api/printer/status`, {
        headers: { Authorization: `Bearer ${token()}`, ...branchHeader() }
      });
      const data = await res.json();
      if (data.data) setStatus(data.data);
    } catch (_) {}
  };

  const handleChange = (index, field, value) => {
    setPrinters(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const handleAdd = () => {
    setPrinters(prev => [...prev, blankPrinter()]);
  };

  const handleRemove = (index) => {
    const removed = printers[index];
    setPrinters(prev => prev.filter((_, i) => i !== index));
    if (removed?.id === defaultCheckoutPrinterId) setDefaultCheckoutPrinterId('');
    if (removed?.id === defaultKitchenPrinterId) setDefaultKitchenPrinterId('');
  };

  const handleSave = async () => {
    if (saving || blocked) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiUrl}/api/printer/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...branchHeader() },
        body: JSON.stringify({ printers, defaultCheckoutPrinterId: defaultCheckoutPrinterId || null, defaultKitchenPrinterId: defaultKitchenPrinterId || null })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'บันทึกการตั้งค่าสำเร็จ' });
        if (data.data?.printers) setPrinters(data.data.printers);
        checkStatus();
      } else {
        setMessage({ type: 'error', text: data.message || 'เกิดข้อผิดพลาด' });
      }
    } catch (_) {
      setMessage({ type: 'error', text: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (printerId) => {
    if (testing[printerId] || blocked) return;
    setTesting(prev => ({ ...prev, [printerId]: true }));
    try {
      const res = await fetch(`${apiUrl}/api/printer/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...branchHeader() },
        body: JSON.stringify({ printerId })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'ส่งคำสั่งทดสอบแล้ว' });
      } else {
        setMessage({ type: 'error', text: data.message || 'ทดสอบไม่สำเร็จ' });
      }
    } catch (_) {
      setMessage({ type: 'error', text: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้' });
    } finally {
      setTesting(prev => ({ ...prev, [printerId]: false }));
    }
  };

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#1d1a12', border: '1px solid #4a3c1c', color: 'var(--warn)', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — หน้านี้แก้ config เครื่องพิมพ์จริงของสาขานั้นทันที
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการตั้งค่าผิดสาขาโดยไม่ตั้งใจ
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div>กำลังโหลดการตั้งค่า...</div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%',
    padding: '0.65rem 0.9rem',
    border: '1px solid var(--border-strong)',
    borderRadius: '8px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    background: 'var(--surface-2)',
    color: 'var(--text)'
  };
  const labelStyle = { display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9rem', color: 'var(--text)' };

  return (
    <div style={{ padding: '1.5rem', maxWidth: '900px', margin: '0 auto' }}>
      <div className="page-header">
        <h2>🖨️ ตั้งค่าเครื่องพิมพ์</h2>
        <button onClick={checkStatus} style={{ background: 'var(--surface-2)', border: '1px solid var(--border-strong)', color: 'var(--text-2)', borderRadius: '8px', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.9rem' }}>
          🔄 ตรวจสอบการเชื่อมต่อ
        </button>
      </div>

      <div style={{
        padding: '0.75rem 1rem',
        marginBottom: '1.25rem',
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        color: 'var(--text)',
        borderRadius: '10px',
        fontWeight: 700,
        fontSize: '0.95rem'
      }}>
        📍 กำลังตั้งค่าเครื่องพิมพ์ของสาขา: {currentBranch?.name ?? '-'}
      </div>

      <Toast message={message} onDismiss={() => setMessage(null)} />

      <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {printers.map((printer, index) => {
          const printerStatus = printer.id ? status[printer.id] : null;
          const connected = printerStatus?.connected;

          return (
            <div key={printer.id ?? `new-${index}`} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {printerStatus && (
                    <span style={{
                      padding: '0.3rem 0.8rem',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      background: connected ? 'rgba(63, 191, 127, 0.15)' : 'rgba(239, 91, 91, 0.15)',
                      color: connected ? 'var(--ok)' : 'var(--danger)'
                    }}>
                      {connected ? '🟢 เชื่อมต่อแล้ว' : '🔴 ไม่ได้เชื่อมต่อ'}
                    </span>
                  )}
                  {printer.id && (
                    <button
                      onClick={() => handleTest(printer.id)}
                      disabled={testing[printer.id]}
                      style={{
                        padding: '0.4rem 0.9rem',
                        borderRadius: '8px',
                        border: '1px solid var(--border-strong)',
                        background: 'var(--surface-2)',
                        color: 'var(--info)',
                        fontWeight: 600,
                        cursor: testing[printer.id] ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        opacity: testing[printer.id] ? 0.6 : 1
                      }}
                    >
                      {testing[printer.id] ? '⏳...' : '🖨️ ทดสอบพิมพ์'}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => handleRemove(index)}
                  style={{ padding: '0.4rem 0.9rem', borderRadius: '8px', border: '1px solid var(--border-strong)', background: 'var(--surface-2)', color: 'var(--danger)', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  🗑️ ลบเครื่องพิมพ์นี้
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={labelStyle}>ชื่อเครื่องพิมพ์</label>
                  <input type="text" placeholder="เช่น เครื่องพิมพ์ครัวข้าว" value={printer.name}
                    onChange={e => handleChange(index, 'name', e.target.value)} className="dark-input" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>ยี่ห้อ (driver)</label>
                  <select value={printer.driver} onChange={e => handleChange(index, 'driver', e.target.value)} className="dark-input" style={inputStyle}>
                    {DRIVERS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>การเชื่อมต่อ</label>
                  <select value={printer.type} onChange={e => handleChange(index, 'type', e.target.value)} className="dark-input" style={inputStyle}>
                    <option value="network">🌐 Network (IP)</option>
                    <option value="usb">🔌 USB</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '1rem' }}>
                {printer.type === 'usb' ? (
                  <div>
                    <label style={labelStyle}>USB Port</label>
                    <input type="text" placeholder="เช่น LPT1 หรือ 1fc9:2016" value={printer.usbPort || ''}
                      onChange={e => handleChange(index, 'usbPort', e.target.value)} className="dark-input" style={{ ...inputStyle, fontFamily: 'monospace' }} />
                    <small style={{ color: 'var(--muted)', display: 'block', marginTop: '0.25rem' }}>
                      พอร์ตปกติ (เช่น LPT1) หรือถ้าเครื่องพิมพ์ไม่ยอมรับข้อมูลผ่าน Windows เลย
                      (พิมพ์ผ่านโปรแกรมทดสอบของผู้ผลิตได้ แต่ทดสอบพิมพ์จากที่นี่ไม่ออก) ให้ใส่
                      รูปแบบ "VID:PID" แทน (เช่น 1fc9:2016 — เช็คได้จาก Zadig หรือ Device Manager)
                      ระบบจะส่งข้อมูลตรงไปที่ USB โดยไม่ผ่าน Windows แต่ต้องสลับไดรเวอร์อุปกรณ์
                      เป็น WinUSB ผ่าน Zadig ก่อนครั้งเดียว ไม่งั้นจะพิมพ์ไม่ออก
                    </small>
                  </div>
                ) : (
                  <>
                    <div>
                      <label style={labelStyle}>IP Address</label>
                      <input type="text" placeholder="เช่น 192.168.1.23" value={printer.ip || ''}
                        onChange={e => handleChange(index, 'ip', e.target.value)} className="dark-input" style={{ ...inputStyle, fontFamily: 'monospace' }} />
                    </div>
                    <div>
                      <label style={labelStyle}>Port</label>
                      <input type="number" min="1" max="65535" value={printer.port || 9100}
                        onChange={e => handleChange(index, 'port', e.target.value)} className="dark-input" style={{ ...inputStyle, fontFamily: 'monospace' }} />
                    </div>
                  </>
                )}
                <div>
                  <label style={labelStyle}>Role (เชื่อมกับหมวดหมู่เมนู)</label>
                  <input type="text" placeholder="เช่น kitchen" value={printer.role || ''}
                    onChange={e => handleChange(index, 'role', e.target.value)} className="dark-input" style={{ ...inputStyle, fontFamily: 'monospace' }} />
                </div>
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--muted)' }}>
                หมวดหมู่เมนูที่ตั้ง "เครื่องพิมพ์" เป็นค่านี้จะถูกส่งมาพิมพ์ที่เครื่องนี้ — ใส่ <code>kitchen</code> หรือ <code>cashier</code> ถ้าต้องการให้ทำงานเหมือนเดิม
              </div>

              {printerStatus && !connected && printer.type === 'network' && (
                <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.9rem', background: '#1d1a12', border: '1px solid #4a3c1c', borderRadius: '8px', fontSize: '0.85rem', color: 'var(--warn)' }}>
                  ⚠️ ไม่สามารถเชื่อมต่อ {printer.ip}:{printer.port} — ตรวจสอบว่าเครื่องพิมพ์เปิดอยู่และอยู่ใน network เดียวกัน
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={handleAdd}
        style={{ width: '100%', padding: '0.85rem', marginBottom: '1.5rem', borderRadius: '10px', border: '2px dashed var(--border-strong)', background: 'var(--surface-2)', color: 'var(--info)', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}
      >
        + เพิ่มเครื่องพิมพ์
      </button>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem', fontSize: '1rem', color: 'var(--text)' }}>ค่าเริ่มต้นของสาขานี้</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>🧾 เครื่องเช็คเงินหลัก</label>
            <select value={defaultCheckoutPrinterId} onChange={e => setDefaultCheckoutPrinterId(e.target.value)} className="dark-input" style={inputStyle}>
              <option value="">(ค่าเริ่มต้น: cashier)</option>
              {printers.filter(p => p.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>🖨️ เครื่องพิมพ์ครัวหลัก</label>
            <select value={defaultKitchenPrinterId} onChange={e => setDefaultKitchenPrinterId(e.target.value)} className="dark-input" style={inputStyle}>
              <option value="">(ค่าเริ่มต้น: kitchen)</option>
              {printers.filter(p => p.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '0.75rem', fontSize: '1rem', color: 'var(--text)' }}>ℹ️ วิธีเชื่อมต่อเครื่องพิมพ์</h3>
        <ol style={{ margin: 0, paddingLeft: '1.4rem', lineHeight: 2, fontSize: '0.9rem', color: 'var(--text-2)' }}>
          <li>ตั้งค่า IP แบบ Static ให้เครื่องพิมพ์ผ่าน router หรือหน้าจอเครื่องพิมพ์</li>
          <li>ตรวจสอบว่าเครื่องพิมพ์ต่ออยู่ใน WiFi เดียวกับเซิร์ฟเวอร์</li>
          <li>กด "+ เพิ่มเครื่องพิมพ์" กรอกชื่อ/ยี่ห้อ/IP แล้วกด "บันทึก"</li>
          <li>กด "ทดสอบพิมพ์" เพื่อตรวจสอบ</li>
        </ol>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="button"
        style={{ width: '100%', padding: '1rem', fontSize: '1.1rem', opacity: saving ? 0.7 : 1 }}
      >
        {saving ? '⏳ กำลังบันทึก...' : '💾 บันทึกการตั้งค่า'}
      </button>
    </div>
  );
}
